import { useMemo, useCallback } from 'react';

import {
  filterByClosedDate,
  isServiceUrgent,
  isServiceToday,
  sortServices,
  SortOption,
} from './MobileServices.utils';
import { ServiceListDataType } from '../../../@types/service.type';
import { ServiceQueryOptions } from '../../../api/services';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { useGetMobileServiceListInfinite } from '../../../queries/service-query';

type DefaultReport = 'myServices' | 'myDepot' | 'myZones';
type BaseQueryOptions = Omit<ServiceQueryOptions, 'page'>;

interface ClientFilters {
  urgentFilter: boolean;
  todayFilter: boolean;
  activeSort: SortOption;
  serviceFilter: string;
}

export interface SectionData {
  services: ServiceListDataType[];
  count: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

export interface UseMobileServiceQueriesReturn {
  allocated: SectionData;
  depot: SectionData;
  zones: SectionData;
  isLoading: boolean;
  isError: boolean;
  refetchAll: () => Promise<void>;
}

const buildQueryOptions = (
  baseOptions: BaseQueryOptions,
  defaultReport: DefaultReport
): BaseQueryOptions => ({
  ...baseOptions,
  defaultReport,
});

const applyClientFilters = (
  services: ServiceListDataType[],
  filters: ClientFilters
): ServiceListDataType[] => {
  let filtered = filterByClosedDate(services, filters.serviceFilter);
  if (filters.urgentFilter) filtered = filtered.filter(isServiceUrgent);
  if (filters.todayFilter) filtered = filtered.filter(isServiceToday);
  return sortServices(filtered, filters.activeSort);
};

export const useMobileServiceQueries = (
  baseQueryOptions: BaseQueryOptions,
  columnSelectionItems: ColumnSelectionItem[],
  clientFilters: ClientFilters
): UseMobileServiceQueriesReturn => {
  const allocatedOptions = useMemo(
    () => buildQueryOptions(baseQueryOptions, 'myServices'),
    [baseQueryOptions]
  );
  const depotOptions = useMemo(
    () => buildQueryOptions(baseQueryOptions, 'myDepot'),
    [baseQueryOptions]
  );
  const zonesOptions = useMemo(
    () => buildQueryOptions(baseQueryOptions, 'myZones'),
    [baseQueryOptions]
  );

  const allocatedQuery = useGetMobileServiceListInfinite(allocatedOptions, columnSelectionItems);
  const depotQuery = useGetMobileServiceListInfinite(depotOptions, columnSelectionItems);
  const zonesQuery = useGetMobileServiceListInfinite(zonesOptions, columnSelectionItems);

  const allocatedRaw = useMemo(
    () => allocatedQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [allocatedQuery.data]
  );
  const depotRaw = useMemo(
    () => depotQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [depotQuery.data]
  );
  const zonesRaw = useMemo(
    () => zonesQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [zonesQuery.data]
  );

  const allocatedFiltered = useMemo(
    () => applyClientFilters(allocatedRaw, clientFilters),
    [allocatedRaw, clientFilters]
  );
  const depotFiltered = useMemo(
    () => applyClientFilters(depotRaw, clientFilters),
    [depotRaw, clientFilters]
  );
  const zonesFiltered = useMemo(
    () => applyClientFilters(zonesRaw, clientFilters),
    [zonesRaw, clientFilters]
  );

  const refetchAll = useCallback(async (): Promise<void> => {
    await Promise.all([allocatedQuery.refetch(), depotQuery.refetch(), zonesQuery.refetch()]);
  }, [allocatedQuery, depotQuery, zonesQuery]);

  return {
    allocated: {
      services: allocatedFiltered,
      count: allocatedFiltered.length,
      hasNextPage: !!allocatedQuery.hasNextPage,
      isFetchingNextPage: allocatedQuery.isFetchingNextPage,
      fetchNextPage: allocatedQuery.fetchNextPage,
    },
    depot: {
      services: depotFiltered,
      count: depotFiltered.length,
      hasNextPage: !!depotQuery.hasNextPage,
      isFetchingNextPage: depotQuery.isFetchingNextPage,
      fetchNextPage: depotQuery.fetchNextPage,
    },
    zones: {
      services: zonesFiltered,
      count: zonesFiltered.length,
      hasNextPage: !!zonesQuery.hasNextPage,
      isFetchingNextPage: zonesQuery.isFetchingNextPage,
      fetchNextPage: zonesQuery.fetchNextPage,
    },
    isLoading: allocatedQuery.isLoading || depotQuery.isLoading || zonesQuery.isLoading,
    isError: allocatedQuery.isError || depotQuery.isError || zonesQuery.isError,
    refetchAll,
  };
};
